// src/features/roles/rolesThunks.ts
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import { getApiEndpoints } from '@/services/api/endpoints';

import type { IRole, RoleScopeType } from '@/domain/entities/role';
import type { CreateItemResponse } from '@/services/api/types';
import { normalizeDeep } from '@/lib/dataSanity';
// import type { CreateItemResponse } from '@/services/api/types';

// =============================
// Types (match your new PK/SK role model)
// =============================
export type ListRolesResponse = {
  roles: IRole[];
  count: number;
};

export type GetRoleRequest =
  | { PK: string; SK: string }
  | { scopeType: RoleScopeType; scopeID: string; roleKey: string }; // roleKey = "ROLE#owner" etc.

export type CreateRoleRequest = {
  scopeType: RoleScopeType;
  scopeID: string;
  name: string;
  permissions: IRole['permissions'];
};

export type UpdateRoleRequest = {
  scopeType: RoleScopeType;
  scopeID: string;
  roleKey: string;
  name?: string;
  permissions?: IRole['permissions'];
};

export type DeleteRoleRequest = {
  PK: string;
  SK: string;
};

// Helpers (client-side keying)
export const makeRolesScopeKey = (scopeType: RoleScopeType, scopeID: string) => `${scopeType}#${scopeID}` as const;

export const makeRoleKey = (PK: string, SK: string) => `${PK}|${SK}` as const;

// =============================
// Thunks
// =============================
export const fetchRoles = createAppAsyncThunk<
  { roles: IRole[] },
  { scopeType: RoleScopeType; scopeID: string; roleKey?: string }
>('roles/fetchRoles', async ({ scopeType, scopeID, roleKey }, { extra: { config }, rejectWithValue }) => {
  const { apiService, logger } = config;

  try {
    const qs = new URLSearchParams();
    qs.set('scopeType', scopeType);
    qs.set('scopeID', scopeID);
    if (roleKey?.trim()) qs.set('roleKey', roleKey.trim());

    const endpoint = `${getApiEndpoints().core.roles.getRole}?${qs.toString()}`;
    logger?.info('[fetchRoles] Fetching from:', endpoint);
    return await apiService.get<{ roles: IRole[] }>(endpoint);
  } catch (err) {
    const error = err as Error;
    logger?.error('[fetchRoles] Error:', error.message);
    return rejectWithValue({ message: error.message });
  }
});

/**
 * Create a custom role in a scope.
 * Backend should support: POST /roles  body: { scopeType, scopeID, name, permissions }
 * Return: IRole (with PK/SK)
 */
export const createRole = createAppAsyncThunk<CreateItemResponse<IRole>, { role: IRole }>(
  'roles/create',
  async ({ role }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.roles.createRole;
      const normalized = normalizeDeep(role);
      logger?.info('[createRole] Creating role:', normalized);

      return await apiService.post<CreateItemResponse<IRole>>(endpoint, normalized);
    } catch (err) {
      const error = err as Error;
      logger?.error('[createRole] Error:', error.message);
      return rejectWithValue({ message: error.message });
    }
  },
);

/**
 * Update an existing custom role.
 * Backend expects: PUT /roles body: { scopeType, scopeID, roleKey, name?, permissions? }
 * Return: IRole
 */
export const updateRole = createAppAsyncThunk<IRole, UpdateRoleRequest>(
  'roles/update',
  async ({ scopeType, scopeID, roleKey, name, permissions }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.roles.updateRole;
      const payload: UpdateRoleRequest = { scopeType, scopeID, roleKey };
      if (typeof name !== 'undefined') payload.name = name;
      if (typeof permissions !== 'undefined') payload.permissions = permissions;

      logger?.info('[updateRole] Updating role:', payload);

      return await apiService.put<IRole>(endpoint, payload);
    } catch (err) {
      const error = err as Error;
      logger?.error('[updateRole] Error:', error.message);
      return rejectWithValue({ message: error.message });
    }
  },
);

/**
 * Delete a role (by PK/SK).
 * Backend should support: DELETE /roles?PK=...&SK=...
 * Return: { PK, SK } so reducer can remove by key
 */
export const deleteRole = createAppAsyncThunk<{ PK: string; SK: string }, DeleteRoleRequest>(
  'roles/delete',
  async ({ PK, SK }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const base = getApiEndpoints().core.roles.deleteRole;

      const qs = new URLSearchParams();
      qs.set('PK', PK);
      qs.set('SK', SK);

      const endpoint = `${base}?${qs.toString()}`;
      logger?.info('[deleteRole] Deleting role:', { PK, SK });

      await apiService.delete(endpoint);
      return { PK, SK };
    } catch (err) {
      const error = err as Error;
      logger?.error('[deleteRole] Error:', error.message);
      return rejectWithValue({ message: error.message });
    }
  },
);
